import clsx from 'clsx'
import { Navigate, useParams } from 'react-router'

import { Icon, OlarkHelper } from '@fv/client-components'
import type { FullShipment, Quote } from '@fv/client-types'

import { Loading } from '@/components/Loading'
import { useLoad, useQuotes } from '@/hooks/shipments'
import { routes } from '@/routes'

import { useAddresses } from '../addresses/useAddress'
import { usePermissions } from '../auth'
import { useRole } from '../auth/hooks'
import { useUnifiedShipmentFlow } from '../route-guides/hooks'
import { BookingForm } from './BookingForm'
import { BookingFormProvider } from './BookingFormProvider'
import { loadCanBeBooked } from './bookFuncs'

type Props = {
  hasError: boolean
  load?: FullShipment
  quote?: Quote
}

const BookingFormContainer = ({ hasError, load, quote }: Props) => {
  const addressesQuery = useAddresses(load)

  if (!load || !quote || addressesQuery.loading) {
    return (
      <div className="bg-white overflow-auto px-6 pt-0 pb-8">
        <Icon
          className={clsx({ 'text-fv-orange': hasError })}
          icon={hasError ? 'exclamation-triangle' : 'sync'}
        />
        <span className="ml-2">
          {hasError
            ? !load
              ? 'Shipment not found.'
              : 'Selected quote is no longer valid for this shipment.'
            : 'Loading...'}
        </span>
      </div>
    )
  }

  if (load.workflow !== 'truckload') {
    return (
      <BookingFormProvider
        addresses={addressesQuery.data}
        load={load}
        quote={quote}
      >
        <BookingForm />
      </BookingFormProvider>
    )
  }

  if (load.workflow === 'truckload') {
    return (
      <Navigate
        replace
        to={routes.bookTruckload({
          quoteRequestId: load.quoteRequestId,
          selectedQuotes: { [load.loadId]: quote._id },
        })}
      />
    )
  }

  return null
}

type Params = {
  loadId: string
  quoteId: string
}

export const BookingPage = () => {
  const { canBook } = usePermissions()
  const role = useRole()
  const unifiedFlow = useUnifiedShipmentFlow()
  const { loadId, quoteId } = useParams<Params>()
  const loadQuery = useLoad(loadId, { refetchWhileQuoting: true })
  const quotesQuery = useQuotes(loadId)
  const isCurrentQuote = loadQuery.data?.selectedQuote?._id === quoteId
  const selectedQuote = isCurrentQuote
    ? loadQuery.data?.selectedQuote
    : (quotesQuery.data?.find(q => q._id === quoteId) as Quote | undefined)

  if (unifiedFlow.isLoading) return <Loading />

  if (unifiedFlow.isEnabled(loadQuery.data?.workflow) && loadId && quoteId) {
    return <Navigate to={routes.vendorBook(loadId, quoteId)} replace />
  }

  if (role !== 'shipper' && !loadCanBeBooked(loadQuery.data)) {
    return <Navigate to={routes.editLoad(loadId)} replace />
  }

  if (
    !selectedQuote &&
    loadQuery.data &&
    loadQuery.data?.status !== 'pending'
  ) {
    return <Navigate to={routes.details(loadQuery.data.loadId)} />
  }

  if (
    (!selectedQuote &&
      loadQuery.data?.quoteProcessStatus === 'rerate-no-compat' &&
      loadQuery.data?.status === 'pending') ||
    (!canBook && loadQuery.data)
  ) {
    quotesQuery.invalidate()
    return <Navigate to={routes.rates(loadQuery.data)} />
  }

  return (
    <>
      <BookingFormContainer
        hasError={loadQuery.isError || quotesQuery.isError}
        load={loadQuery.data}
        quote={selectedQuote}
      />
      <OlarkHelper loadId={loadQuery.data?.loadId} />
    </>
  )
}
